import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, unpauseDecomForUser } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import GeneralDialog from '../../../../shared/general-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { DecomSuspendedUserModel } from '../../../../../types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface UnpauseUserDecomDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userDecomStatus: DecomSuspendedUserModel;
  changeLogRoles: string[];
}

export default function UnpauseUserDecomDialog({
  open,
  handleClose,
  userEmail,
  userDecomStatus,
  changeLogRoles,
}: UnpauseUserDecomDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => unpauseDecomForUser(userEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User decom processing is now unpaused!',
      });
      const unpausedDecomModel = {
        ...userDecomStatus,
        isactive: true,
      };
      queryClient.setQueryData(
        [UserKeys.DecomStatus, userEmail],
        unpausedDecomModel
      );
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SuspendedUsers],
      });
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue unpausing the users decom processing. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.unpause_decom, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.userDetails,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.UnpauseDecomForUser.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title={'Unpause User Decom'}
      dialogContent={
        <Box sx={{ px: 4 }}>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Do you wish to unpause decom processing for {userEmail}?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Update',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
